import React from 'react';
import {
  Box, Typography, Button, Paper, IconButton, Autocomplete, TextField,
  Tooltip, Alert, Checkbox, FormControlLabel, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ---------------------------------------------------------------------------
// ScopeEditor — multi-clause allowed_actions builder.
//
// Renders a list of <ScopeClause> entries the partner is building. The
// underlying wire shape is `{ allowed_actions: string[], data_scope: object }[]`
// (see platform/common-core/.../ScopeClause.java). The TokenScope-matching
// rule (access-profile §4.4): an action is permitted if ANY clause's
// allowed_actions covers it AND the clause's data_scope matches the row.
//
// v1 scope (kept simple intentionally — handoff doc § Step 5):
//   - Per clause: allowed_actions multi-select with the standard verb
//     catalog as suggestions + freeSolo for custom partner verbs
//   - Per clause: data_scope is fixed to {} (no UI). Partners needing
//     row-level filters use the POST /v1/app-contexts/{ctx}/profiles
//     endpoint directly. Surfaced via the Alert below.
//   - Add Clause / Remove Clause controls
//
// Future MR #6 work — extracted to @vectros-ai/react — will add a
// data_scope key/value picker. For now, the simpler shape covers the
// common "agent with admin:keys + read on everything" pattern.
//
// `granted_capabilities` (0.40.0, fern/CHANGELOG.md § 0.40.0 "Added") —
// a clause-level array separate from allowed_actions/data_scope, closed to
// `member-lifecycle` / `forensic-read` / `context-directory-read` /
// `delegate-mint`. See KNOWN_CAPABILITIES below for which two of the four
// this editor offers, and why.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// The `granted_capabilities` names THIS EDITOR offers to author. A
// SUBSET of the platform's closed four-name list (Capability.java), narrowed
// to the ones the developer portal's OWN session can ever back.
//
// The platform enforces a subset-of-caller rule on every scope-authoring
// write: a clause may name a capability only if the calling credential's own
// scope names it too (ADR-0082 D3). The dev portal's browser session is
// always minted through GET /developer/scoped-token (AuthContext.js) — the
// Cognito-authenticated developer's OWN token, never a sub-user's (the
// developer portal has no sub-user concept; every session here is the
// account's Dev Admin). That route's `wildcardScope()` (DeveloperScopedTokenHandler)
// is the SAME mint used by ui/admin-app's OWNER sessions, and hardcodes
// `granted_capabilities: ["member-lifecycle", "delegate-mint"]` — so, traced
// rather than assumed (the issue explicitly warns not to copy admin-app's
// set), the two portals' sessions land on the identical two names for the
// identical reason: it is what the SAME mint path already grants. `forensic-read`
// and `context-directory-read` are deliberately withheld from that mint (see
// wildcardScope()'s own derivation comment) — offering either here would ship
// a checkbox that always fails server-side, regardless of who clicks it.
//
// An unrecognized name denies the WHOLE clause server-side rather than being
// ignored (ADR-0082 D2 rule 2), so this editor never offers one outside this
// list — toggling only ever adds/removes an exact known name, which is what
// keeps a name outside THIS list (e.g. a future-release name) untouched on
// save for a clause that already carried it.
// ---------------------------------------------------------------------------
export const KNOWN_CAPABILITIES = [
  {
    value: 'member-lifecycle',
    label: 'member-lifecycle — create and remove identities in your tenant',
  },
  {
    value: 'delegate-mint',
    label: 'delegate-mint — mint a scoped API key bound to a different principal than your own',
  },
];

const KNOWN_CAPABILITY_VALUES = KNOWN_CAPABILITIES.map((c) => c.value);

/**
 * Canonical order for a capabilities array: known names in catalog order,
 * then anything this editor doesn't offer (an unrecognized/future name, or
 * one this editor deliberately excludes — see KNOWN_CAPABILITIES), in their
 * original relative order. Mirrors admin-app's ScopeEditor.tsx equivalent,
 * and for the same reason: without it, an uncheck-then-recheck round-trip
 * can reorder the array (toggling always appends at the end) and read as a
 * spurious edit against a JSON.stringify-based dirty-state comparison even
 * though nothing actually changed.
 */
function canonicalizeCapabilities(capabilities) {
  const known = KNOWN_CAPABILITY_VALUES.filter((v) => capabilities.includes(v));
  const rest = capabilities.filter((v) => !KNOWN_CAPABILITY_VALUES.includes(v));
  return [...known, ...rest];
}

// ---------------------------------------------------------------------------
// `assignable_roles` (0.43.0, #1185) — roleIds a clause may compose into a
// delegated access profile. Orthogonal to allowed_actions/data_scope: it
// narrows WHICH named roles this clause's authority may hand out, not how
// much data it reaches.
//
// **Absent means two opposite things, and conflating them is the trap.** At
// ENFORCEMENT, absent is "no restriction" — the permissive default. At
// AUTHORING it is the MAXIMAL value: a caller whose own covering clause is
// itself restricted may not save a clause that omits the field, because
// claiming "unrestricted" is strictly wider than any list they could offer.
// An authored EMPTY list is rejected outright on both paths — see
// platform/common-core's ScopeClause#assignable_roles javadoc for the full
// enforcement story (TokenScope#assignableRolesAuthoredWithinCaller).
//
// This is exactly the lockout #1185 exists to close: before this field
// existed in this editor, a restricted account's principal could never save
// ANY new clause here (Save was refused every time), since emptyClause()
// omitted the field unconditionally and there was no way to author a real
// list instead.
//
// roleId format the platform validates against (RoleDB.ROLE_ID_PATTERN): a
// lowercase letter first, then lowercase letters/digits/hyphens, 3-31
// characters total. Mirrored here (not imported — the platform's regex is
// Java) so a freeSolo-typed entry is rejected client-side before a save
// round-trip does it server-side.
export const ROLE_ID_PATTERN = /^[a-z][a-z0-9-]{2,30}$/;

// Structural cap on assignable_roles' size — mirrors
// ScopeClause.MAX_ASSIGNABLE_ROLES (platform-side).
export const MAX_ASSIGNABLE_ROLES = 20;

// Standard verb catalog — access-profile/design.md §4.5. Surfaced as
// Autocomplete suggestions; partners can also type custom verbs.
//
// Beyond these named verbs, allowed_actions also accepts the compact
// `resource:letters[:qualifier]` form (e.g. `documents:cru`). The letters are
// c/r/u/d plus `s` for SENSITIVE REVEAL (PHI un-masking), granted per
// record-type — e.g. `records:rs:patient` = read + reveal on the `patient`
// type. Reveal is fail-safe: absent `s`, sensitive fields come back
// "[redacted]". It's per-type (not a fixed string), so it's documented here as
// a typeable compact form rather than a fixed catalog entry. (#114)
// 0.43.0 added a SIXTH letter, `x` (execute), carried only by `scripts` — and
// with it a qualifier that is NOT a type: `scripts:x:<name>` narrows to one
// script by NAME. So the trailing segment is read against the resource, not
// against one fixed axis; COMPACT_FORM_HINT says so in the user-facing copy.
// #468: the catalog uses the compact resource:ops form the authorizer honors.
// Bare read/write/delete and admin:keys/admin:profiles were REMOVED — they matched
// nothing at runtime (a colon-less verb is skipped; admin:keys parsed as resource
// "admin"), so the backend now rejects them at save. Data-plane verbs are the
// resource:ops form (c/r/u/d + the s reveal letter — see COMPACT_FORM_HINT).
export const STANDARD_VERB_CATALOG = [
  '*',
  'records:cru',
  'records:r',
  'documents:r',
  'folders:cru',
  'search:r',
  'schemas:r',
  // scripts:crd = push/read/delete script object versions. No `u`: scripts are
  // immutable per version; pushing a new version is a create, not an update.
  'scripts:crd',
  // scripts:x = EXECUTE a stored script (POST /v1/scripts/execute, 0.43.0). A
  // separate letter from c/r/u/d, and deliberately separate from `scripts:c`:
  // authoring a script and running one are different grants. Promoted from the
  // free-text escape hatch to a one-click preset in 0.43.0, when the endpoint it
  // gates actually shipped — before that there was nothing to execute.
  // Narrowable to one script by name: `scripts:x:<name>` (see COMPACT_FORM_HINT).
  'scripts:x',
  // triggers:crud = declare/read/reconcile/delete async trigger rules. Unlike
  // scripts, a trigger rule is upsert-based, so it carries the full crud verb set.
  // As of 0.43.0 a declared rule FIRES — a rule's `scopes`/`roleIds` are live
  // authority executed under, not an inert declaration, so granting `triggers:c`
  // grants the ability to put a running grant into the tenant.
  'triggers:crud',
  // keys:crd = manage scoped API keys (was admin:keys). profiles:cru = manage
  // access profiles/templates (was admin:profiles). app-contexts:cru = context
  // namespace lifecycle (ADR-0039); whole-context DELETE is root-only, not grantable.
  'keys:crd',
  'profiles:cru',
  'app-contexts:cru',
  // users:crud = manage partner users + sub-user invitations (was admin:users, which is not a
  // grantable verb — #468/#755). The invite RESEND act needs users:r + users:u, so these must be
  // selectable here; before #755 they were reachable only via the free-text escape hatch.
  'users:crud',
  'logs:r',
];

// Compact-form hint shown beside the verb picker so the letters and qualifiers
// the catalog above can't enumerate stay discoverable in the UI.
//
// Two letters beyond c/r/u/d: `s` = SENSITIVE REVEAL (PHI un-masking, per record
// type) and, as of 0.43.0, `x` = EXECUTE, which exists only on `scripts`.
//
// The trailing qualifier is NOT one axis. It is read against the resource: on
// records/documents it names a TYPE, and on `scripts:x` it names a SCRIPT NAME
// (`scripts:x:daily-rollup` = execute that one script, every version). Spelling
// the qualifier as "[:type]" alone would send a partner looking for a record type
// that does not exist on the execute grant.
// Kept short on purpose: this renders as MUI helperText under the field, INSIDE
// the per-clause map, so a 3-clause profile shows it three times in a modal.
//
// ⚠️ THE QUALIFIER IS FOUR SEPARATE AXES, and pairing the wrong resource with the
// wrong one is REJECTED AT SAVE — so this string may not generalise across them.
// From ScopeAction (the deciding artifact, not this comment):
//
//   QUALIFIABLE_RESOURCES          {records, entities}          any c/r/u/d letters
//   SENSITIVE_REVEAL_RESOURCES     {records, entities,          qualifier legal ONLY
//                                   documents, users}            if EVERY letter is `s`
//   PRINCIPAL_QUALIFIED_RESOURCES  {profiles}                   letters c/u/d only
//   SCRIPT_EXECUTE_QUALIFIED       {scripts}                    letter `x` only
//
// An earlier version of this hint said the qualifier "narrows to a record type on
// records/DOCUMENTS (records:rs:patient)". The example is valid; the sentence is
// not. `documents` is in the sensitive-reveal set ONLY, so `documents:rs:invoice`
// — the direct analogue a partner would form from that sentence — is refused.
// ScopeAction's own javadoc names that exact string as rejected. This is the same
// failure the hint was rewritten to fix one axis over, which is why it is worth
// the extra words rather than a shorter half-truth.
//
// `profiles`' principal axis (profiles:c:usr_… / :self) is deliberately NOT in the
// user-facing string — omitting an axis is not a false claim, and four axes in one
// helperText is unreadable. The table above is the complete picture for authors.
export const COMPACT_FORM_HINT =
  'Advanced: resource:letters[:qualifier]. Letters are c/r/u/d, plus "s" to reveal '
  + 'sensitive/PHI fields and "x" to execute (scripts only). What the qualifier means '
  + 'depends on the resource: a record type or entity namespace on records/entities '
  + '(records:rs:patient), a script NAME on scripts:x (scripts:x:daily-rollup). On '
  + 'documents and users a qualifier is reveal-only (documents:s:invoice).';

/**
 * Build a fresh empty clause. allowed_actions is an empty list (the
 * editor will validate at submit time); data_scope is `{}` (applies to
 * all data within the tenant per access-profile §4.4); granted_capabilities
 * is `[]` (kept on the shape, not omitted, so a clause round-trips
 * byte-for-byte through load → save when untouched, same reasoning as
 * admin-app's ScopeEditor.tsx equivalent). assignable_roles is deliberately
 * ABSENT rather than `[]`: an empty list is rejected outright by the
 * platform (see the module doc above), and the picker below never emits
 * `[]` either — an empty selection maps back to "field omitted", the same
 * state this function starts from.
 */
export function emptyClause() {
  return { allowed_actions: [], data_scope: {}, granted_capabilities: [] };
}

/**
 * Validate the supplied clause list. Returns null if valid, or a string
 * error message identifying the first problem found. Mirrors backend
 * `ScopeClause.validate` logic + the AccessProfile "at least one clause"
 * requirement.
 */
export function validateClauses(clauses) {
  if (!clauses || clauses.length === 0) {
    return 'At least one scope clause is required';
  }
  for (let i = 0; i < clauses.length; i++) {
    const c = clauses[i];
    if (!c.allowed_actions || c.allowed_actions.length === 0) {
      return `Clause ${i + 1}: at least one allowed action is required`;
    }
    for (let j = 0; j < c.allowed_actions.length; j++) {
      const a = c.allowed_actions[j];
      if (!a || typeof a !== 'string' || !a.trim()) {
        return `Clause ${i + 1}, action ${j + 1}: must be a non-blank string`;
      }
    }
    // #1185 — assignable_roles: format + count, mirroring the platform's own
    // ScopeClause.validateAssignableRoles order (that method runs last too).
    // No duplicate check: the picker de-dupes on every change (see
    // setAssignableRoles below), so nothing it can produce ever reaches this
    // validator already holding one.
    if (c.assignable_roles && c.assignable_roles.length > 0) {
      if (c.assignable_roles.length > MAX_ASSIGNABLE_ROLES) {
        return `Clause ${i + 1}: at most ${MAX_ASSIGNABLE_ROLES} composable roles can be listed on one clause`;
      }
      for (let k = 0; k < c.assignable_roles.length; k++) {
        const rid = c.assignable_roles[k];
        if (!rid || !ROLE_ID_PATTERN.test(rid)) {
          return `Clause ${i + 1}: "${rid}" isn't a valid roleId — lowercase letters, digits and - only, 3-31 characters, starting with a letter`;
        }
      }
    }
  }
  return null;
}

/**
 * Controlled multi-clause editor.
 *
 * @param {object} props
 * @param {Array<{allowed_actions: string[], data_scope: object, granted_capabilities?: string[]}>} props.value
 *   Current list of clauses. Caller owns the state.
 * @param {(next: Array) => void} props.onChange  Replaces the whole list.
 */
export default function ScopeEditor({ value, onChange }) {
  const clauses = value || [];

  const updateClause = (idx, patch) => {
    const next = clauses.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange(next);
  };

  const toggleCapability = (idx, name) => {
    const current = clauses[idx]?.granted_capabilities || [];
    const next = current.includes(name)
      ? current.filter((c) => c !== name)
      : [...current, name];
    updateClause(idx, { granted_capabilities: canonicalizeCapabilities(next) });
  };

  // #1185 — assignable_roles authoring. `undefined` (not `[]`) removes the
  // restriction: the platform rejects an authored empty list outright (see
  // the module doc above), so an all-removed selection has to omit the key
  // entirely rather than send `[]`. Rebuilding the clause explicitly (not
  // `updateClause`'s spread-a-patch-in) is what makes that possible — a
  // spread can only add/overwrite a key, never delete one, so a patch of
  // `{ assignable_roles: undefined }` would still leave the key present with
  // an `undefined` value on the object (harmless for this component's own
  // `JSON.stringify`-at-save path, since JSON.stringify drops undefined-
  // valued keys, but worth being explicit about rather than relying on it).
  const setAssignableRoles = (idx, next) => {
    // De-dupe defensively (freeSolo text entry can retype an id already
    // picked) while preserving first-seen order, so validateClauses never
    // has to reject an authored duplicate — this handler just never
    // produces one.
    const deduped = [];
    for (const raw of next) {
      const rid = (raw || '').trim();
      if (rid !== '' && !deduped.includes(rid)) deduped.push(rid);
    }
    const patched = { ...(clauses[idx] || {}) };
    if (deduped.length > 0) {
      patched.assignable_roles = deduped;
    } else {
      delete patched.assignable_roles;
    }
    onChange(clauses.map((c, i) => (i === idx ? patched : c)));
  };

  const addClause = () => {
    onChange([...clauses, emptyClause()]);
  };

  const removeClause = (idx) => {
    onChange(clauses.filter((_, i) => i !== idx));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info" icon={<InfoOutlinedIcon />}>
        <Typography variant="caption">
          Pick one or more verbs per clause. <code>*</code> grants all actions
          (OWNER default) but confers no named capability — capabilities must be
          named explicitly, below. Data-row filters (e.g. "only org_engineering
          rows") aren't editable here — use{' '}
          <code>POST /v1/app-contexts/{'{ctx}'}/profiles</code> for those. Of the
          four named platform capabilities, only <code>member-lifecycle</code> and{' '}
          <code>delegate-mint</code> are offered below — this editor's own session
          can't back <code>forensic-read</code> or{' '}
          <code>context-directory-read</code>, so a clause naming either through
          this UI is refused at save. Your root API key isn't subject to that
          limit — it can still author either directly via{' '}
          <code>POST /v1/app-contexts/{'{ctx}'}/profiles</code> if you need one of
          them.
        </Typography>
      </Alert>

      {clauses.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            color: 'text.secondary',
            borderStyle: 'dashed',
          }}
        >
          <Typography variant="body2">No clauses yet — click "Add clause" below</Typography>
        </Paper>
      )}

      {clauses.map((clause, idx) => (
        <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Clause {idx + 1}
            </Typography>
            <Tooltip title="Remove clause">
              <IconButton size="small" aria-label="Remove clause" onClick={() => removeClause(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Autocomplete
            multiple
            freeSolo
            options={STANDARD_VERB_CATALOG}
            value={clause.allowed_actions || []}
            onChange={(_, v) => updateClause(idx, { allowed_actions: v })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Allowed actions"
                placeholder="Pick from catalog or type a custom verb"
                helperText={COMPACT_FORM_HINT}
                size="small"
              />
            )}
          />

          {/* granted_capabilities. A closed 2-name list (see
              KNOWN_CAPABILITIES above), so this is checkboxes rather than the
              allowed_actions Autocomplete's freeSolo pattern: there is no
              custom-capability escape hatch, by design (ADR-0082 D2 rule 2 —
              an unrecognized name denies the whole clause). */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Platform capabilities (optional)
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {KNOWN_CAPABILITIES.map((cap) => (
                <FormControlLabel
                  key={cap.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={(clause.granted_capabilities || []).includes(cap.value)}
                      onChange={() => toggleCapability(idx, cap.value)}
                    />
                  }
                  label={<Typography variant="body2">{cap.label}</Typography>}
                />
              ))}
            </Stack>
          </Box>

          {/* assignable_roles (0.43.0, #1185) — restricts which named roles
              this clause may compose into a delegated access profile.
              freeSolo, same shape as allowed_actions above: there is no
              context-roles query in this dialog to source suggestions from
              (unlike admin-app's editor), and a clause can legitimately name
              a role this session has no list of anyway. */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Composable roles (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
              Restricts which named roles this clause may compose into a delegated access
              profile — separate from, and narrower than, the actions above. Leaving this
              empty grants the default: this clause may compose ANY role. One thing to know
              before you leave it empty: if YOUR OWN account is itself restricted this way,
              this clause's save will be refused unless you explicitly list every role it
              needs — an empty selection here is read as "no restriction", which is wider
              than what a restricted account can grant, not as "restricted to nothing".
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={clause.assignable_roles || []}
              onChange={(_, v) => setAssignableRoles(idx, v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roles this clause may compose"
                  placeholder={
                    (clause.assignable_roles || []).length === 0
                      ? 'Type a roleId, press Enter'
                      : undefined
                  }
                  size="small"
                />
              )}
            />
          </Box>
        </Paper>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addClause}
          sx={{ textTransform: 'none' }}
        >
          Add clause
        </Button>
      </Box>
    </Box>
  );
}
